/**
 * Block guardrails — enforcement primitives.
 *
 * A guardrail is a copy limit declared BY the SFMC block itself, not by this
 * app. See services/sfmcBlocks.ts for where they're read from. This module is
 * pure (no fetch, no DOM) so it can be unit-tested and reused by both the
 * inspector inputs and the render path.
 *
 * The `checkPattern` import is not a cycle: echPattern.ts takes only the
 * `GuardrailField` TYPE from here, and a type-only import is erased at runtime.
 *
 * The contract, ported from the production monolith:
 *   • a cap is EITHER `max` characters OR `lines × cpl` (chars-per-line)
 *   • enforcement is hard — the value is clamped, never merely flagged
 *   • clamping happens on every write path AND again at render, so a value
 *     that arrives by paste, inline edit or dynamic fill still obeys the rule
 */
import { checkPattern } from "./echPattern";


/** One field's rule, exactly as the SFMC block declares it. */
export interface GuardrailField {
  /** hard character cap */
  max?: number;
  /** line budget — combines with `cpl` to produce the cap */
  lines?: number;
  /** characters per line (defaults to 24 when a line budget is given) */
  cpl?: number;
  /** this field may keep hand-typed line breaks (default: they are dropped, echBreaks.ts) */
  allowBreaks?: boolean;
  /** body copy may start • bullet lines with Enter (default: on for paragraph fields, echBreaks.ts) */
  bullets?: boolean;
  label?: string;
  /**
   * Control the block DECLARES for this field. Layne's live blocks use `tel` and
   * `number` too (verified against the 2026-08-18 block cache: ECH_Logo.phone is
   * `tel`, ECH_MainImage.imgSrc is `image`), and those four types are exactly the
   * ones the monolith EXCLUDES from cap enforcement — see echLimits().
   */
  type?: "textarea" | "text" | "url" | "image" | "tel" | "number" | "select";
  /**
   * The allowed values on a `select` field (2026-08-20).
   *
   * Layne's live `ECH_CopyBlock` declares `HeadlineSize` as
   * `{type:"select", options:[16,18], default:"18"}` — the headline's font size in
   * px. This type was missing here, so `echFields` fell through to a free-TEXT box
   * and Jack could type "dfdfd" into a font size; that value then substitutes
   * straight into the block's `font-size:{{HeadlineSize}}` and the email ships
   * broken CSS. Numbers arrive as numbers from the worker's JSON, hence the union.
   */
  options?: readonly (string | number)[];
  /**
   * The `ECH_LIST_*` BLOCK to source this field's options from (Layne's spec,
   * 2026-08-27) — a block NAME, deliberately, not an inline list. A second
   * controlled vocabulary later (amenity captions, the sq ft / beds / baths unit
   * labels) is then a new `ECH_LIST_` block plus this one key, with no code change.
   *
   * Wins over `options` when both are declared: the block is the source of truth,
   * and an inline copy left on a field would be the same silent-drift problem that
   * moving the CTA list out of this repo exists to solve.
   */
  optionsFrom?: string;
  /**
   * The author may not type a custom value — list only. Only meaningful alongside
   * `options` or `optionsFrom`; on its own it governs nothing.
   */
  strict?: boolean;
  /** declared pixel size on an `image` field (monolith → FieldDescriptor w/h) */
  width?: number;
  height?: number;
  /**
   * Declared format, e.g. `^\d{3}-\d{3}-\d{4}$`.
   *
   * ENFORCED AS A WARNING since 2026-08-24. It was carried-but-ignored for monolith
   * parity — the old CloudPage never checked it, so neither did the port. Layne
   * authored the first real one on 2026-08-24 (sentence case on `ECH_CopyBlock`'s
   * Headline) and asked why nothing happened; nothing had ever used the key.
   *
   * WARNING, never a block: an over-strict pattern must not be able to stop a send.
   * Her first one rejected `$5,000 toward your closing costs`, a curly-quoted
   * headline pasted from Word, and any two-line headline — on a field that declares
   * `lines: 2`. Those are the copy divisions really write, so the cost of a bad
   * pattern has to stay "a hint someone can ignore".
   */
  pattern?: string;
  /**
   * ── WHERE A PATTERN DOES BLOCK (2026-09-09) ──────────────────────────────────────
   * `dataRequired` on the field promotes its `pattern` from a warning to a send-block,
   * and nothing else does. Layne asked for a malformed link to stop a send rather than
   * warn-and-send, and her test for why that is safe is the right one: present-but-
   * invalid cannot be produced by an outage, a pending lookup or a missing row. Kept
   * opt-in per field because 88 patterns are declared and flipping them all would
   * re-open the ruling this comment documents. `patternBlocks` is the synonym if she
   * prefers the two decoupled. Gate: `echDataGate.dataGateBlanks`.
   */
  dataRequired?: boolean;
  patternBlocks?: boolean;
  /**
   * What to tell the requester when `pattern` fails (2026-08-24, Layne's key).
   *
   * She shipped this alongside her first pattern — "Use sentence case — start with a
   * capital letter and do not type the headline in all caps" — which is the whole
   * reason patterns are usable by non-engineers: the author of the rule writes the
   * sentence, not us guessing at intent from a regex. Falls back to a generic line
   * when a pattern arrives without one.
   */
  patternMessage?: string;
  /** starting value when the block is added */
  default?: string;
  /** where this value COULD come from, e.g. "community.name" — enables the
   *  Manual|Dynamic toggle. Absent means manual-only. */
  source?: string;
  /** override the `{{key}}` placeholder if the block uses a different token */
  token?: string;
  /**
   * THE DATA-EXTENSION COLUMN THIS FIELD FILLS FROM (Layne, 2026-09-02).
   *
   * Only meaningful on a block whose header declares `dataSource`. Case-insensitive on
   * purpose: the rowset API lowercases every column name regardless of how the DE
   * declares it (`Headshot1_Url` arrives as `headshot1_url`), so the declaration is
   * written in the DE's casing and matched without it.
   */
  fromColumn?: string;
  /**
   * ── READ FROM THE DECLARATION, NEVER INFERRED FROM THE TYPE (Layne, BLOCKER-REQUIRED-
   * BY-TYPE-20260902) ─────────────────────────────────────────────────────────────────
   * `required`: whether a blank here should stop a send. Declared `false` means NOT
   * required whatever the type; declared `true` means required whatever the type;
   * undeclared falls back to the type default the Done gate has always used (url and
   * image gate, everything else does not). Her 17 DE-fed Contact Us fields declared
   * `required: false` and the gate demanded eight of them anyway, because it read the
   * type and never looked — the same shape as the pattern bug #86 fixed.
   */
  required?: boolean;
  /**
   * `authorEditable: false` — the author cannot type into this field (the division's
   * record fills it). It is therefore NEVER something the author is asked to fill,
   * whatever `required` says, and the panel shows it read-only.
   */
  authorEditable?: boolean;
}

/**
 * Is a blank in this field something the AUTHOR must fill before sending?
 *
 * The one place the answer is decided (echRequired, the form's submit belt and the
 * Inspector's link-miss set all go through `requiredBlanks`, which calls this). The
 * declaration wins; the type is only the fallback for a field that declares nothing.
 */
export function fieldRequired(f: GuardrailField | undefined, typeDefault: boolean): boolean {
  if (!f) return typeDefault;
  if (f.authorEditable === false) return false;
  if (typeof f.required === "boolean") return f.required;
  return typeDefault;
}

/**
 * A DATA EXTENSION THE BLOCK FILLS ITSELF FROM (Layne's Contact Us ask, 2026-09-02).
 *
 * "Read a data extension keyed on the author's division selection and populate the
 * block's declared fields from the matching row." Declared in the block's own header so
 * the app never has to know a block by name — any block that declares one is fed by it,
 * which is the generic capability she asked for rather than "support Contact Us".
 *
 * `externalKey`, never a name: a DE can be renamed in SFMC without warning.
 * `primaryKey` is the column matched against the author's division code; defaults to
 * `Division_Code`. The scope value comes from Step 1, the same as every other feed.
 */
export interface DataSource {
  externalKey: string;
  primaryKey?: string;
  /** the DE's own name, used by the gate's `{{DataSource}}` token so Layne never
   *  retypes it into a message (2026-09-09) */
  name?: string;
  /**
   * THE GATE'S SENTENCES, OWNED BY THE BLOCK'S AUTHOR (2026-09-09).
   *
   * Layne: "read these from the block rather than holding them in code… if they live in
   * your renderer, we can't reword them without a deploy." `blockMessages` is accepted
   * as a synonym. Tokens: `{{Division}}` `{{DataSource}}` `{{Column}}` `{{Field}}` and,
   * on `invalidValue` only, `{{Value}}`. Defaults live in `echDataGate` and still block
   * when this is absent or the header failed to parse.
   */
  messages?: Partial<Record<"noRow" | "emptyColumn" | "invalidValue", string>>;
  blockMessages?: Partial<Record<"noRow" | "emptyColumn" | "invalidValue", string>>;
}

/** The whole guardrail payload parsed out of a block's @GUARDRAILS comment. */
export interface Guardrails {
  /** unique key identifying which block this applies to */
  module: string;
  label?: string;
  /** the left-panel group this block belongs in ("Promo banners") — m2Blocks.paletteGroups */
  panelGroup?: string;
  source?: string;
  /** locked image dimensions, if the block has one */
  image?: { w: number; h: number; alts?: string[] } | null;
  /** the ONE line a locked block's read-only card shows instead of a panel
   *  (monolith `g.lock` → `M[key].lockNote`) */
  lock?: string;
  /** plain statements shown to the user */
  notes?: string[];
  /** must-do-before-send items */
  actions?: string[];
  /** top-level editable fields */
  fields?: Record<string, GuardrailField>;
  /** same, for repeating sub-items (cards, rows) */
  sub?: Record<string, GuardrailField>;
  /**
   * HOW MANY OF THIS BLOCK AN EMAIL MAY HOLD. 1 = place once, 5 = up to five,
   * absent = uncapped.
   *
   * Declared in the block's own `@GUARDRAILS` header, which is the point: the cap
   * lives next to the rest of the block's rules, a new block is governed the moment
   * Layne creates it, and non-1 limits become expressible (the featured pair needs 5,
   * not "1 or unlimited"). Read by `blockCaps.capOfBlockType`.
   *
   * WHY THIS EXISTS: enforcement used to come from a hardcoded list in
   * `singletonBlocks.ts`, so Layne added `maxCount` to all 26 blocks on 2026-08-26 and
   * nothing changed — nine were already capped by the list and stayed capped,
   * seventeen stayed uncapped, and setting `maxCount: 2` on ECH_SocialIcons still
   * blocked the second copy. Her report: `MAXCOUNT-NOT-READ-REPORT.md`.
   */
  maxCount?: number;
  /**
   * WHERE THE BLOCK IS ALLOWED TO SIT (declared 2026-08-27, read by `echPlacement.ts`).
   * `parseGuardrails` returns the whole parsed header, so this arrived all along and was
   * simply undeclared here — Layne: "Logo, Contact Us, Social and Disclaimer can be
   * moved." Shape verbatim off the live pull; `position` may be "first", "last", or an
   * integer, and a NEGATIVE integer counts from the end.
   */
  placement?: {
    locked?: boolean;
    position?: "first" | "last" | number;
    mustImmediatelyFollow?: string;
    mustImmediatelyPrecede?: string;
    movesWith?: string;
    message?: string;
  } | null;
  /** the data extension this block's `fromColumn` fields fill from — see `DataSource` */
  dataSource?: DataSource | null;
  /** which template this block belongs to, and its position in it */
  template?: string;
  order?: number;
  /**
   * MANY templates, with this block's position in each — the shape `template` +
   * `order` cannot express (ECH_MainImage is position 5 in five templates and 3
   * in two). When a block declares this, SFMC's placement WINS over the template
   * definition block's `blocks[]` order (monolith `echSfmcSeq`, and `__grSync`
   * L13148 `tplMap:(g.templates && typeof g.templates==="object") ? g.templates : null`).
   */
  templates?: Record<string, number> | null;
}

export type CapState = "ok" | "near" | "over";

const DEFAULT_CPL = 24;

/** Resolve a rule to a single character cap. Returns 0 when uncapped. */
export function capOf(rule?: GuardrailField | null): number {
  if (!rule) return 0;
  if (rule.max) return rule.max;
  /* A DECLARED 0 FALLS BACK, IT DOES NOT COLLAPSE THE CAP (code review #18). The
     monolith's `__grCap` is `(r.lines||1) * (r.cpl||24)` (index.html L12858), so
     {lines:3, cpl:0} caps at 72 and {lines:0, cpl:30} at 30. This returned 0 —
     "uncapped" — for both, while `echLimits` would still admit such a field
     (`!= null` passes on 0). Latent only: no field in the 2026-08-18 block cache
     declares a zero. Aligned rather than left as a trap for the first one that does. */
  if (rule.lines != null || rule.cpl != null) return (rule.lines || 1) * (rule.cpl || DEFAULT_CPL);
  return 0;
}

/** Human wording for a breached rule — matches the inspector's inline message. */
export function limitMessage(rule?: GuardrailField | null): string {
  if (!rule) return "Limit reached";
  const limit = rule.lines
    ? `${rule.lines} line${rule.lines > 1 ? "s" : ""}`
    : `${capOf(rule)} characters`;
  return `Limit reached — ${limit} max`;
}

/** ok / near (>90%) / over. Used for the counter's colour. */
export function capState(value: string | undefined, rule?: GuardrailField | null): CapState {
  const cap = capOf(rule);
  if (!cap) return "ok";
  const n = (value ?? "").length;
  if (n >= cap) return "over";
  if (n > cap * 0.9) return "near";
  return "ok";
}

/**
 * The ECH LABEL-ROW readout (monolith `__echCntTxt`, index.html L12870–12875).
 *
 * "64/325" while there is room, "10 left" as it tightens, "Limit reached" at the
 * cap. Same numbers `capState`/`capOf` already produce — this only decides which
 * of them the label row shows. Empty string when the block declares no cap.
 *
 * Why it exists at all: the under-box `.grc` is hidden while the state is `ok`
 * (`.grc.ok{display:none}`, monolith L780), so without this a division never
 * learned a cap existed until they were nearly over it — Jack's "no guardrails
 * and etc". The Aug 13 call asked for the opposite: limits surfaced in the
 * visualizer. This is a DISPLAY change only; every cap is still read at runtime
 * from the SFMC block's own comment.
 */
export function echCntTxt(value: string | undefined, rule?: GuardrailField | null): string {
  const cap = capOf(rule);
  if (!cap) return "";
  const n = (value ?? "").length;
  const st = capState(value, rule);
  if (st === "over") return "Limit reached";
  if (st === "near") return Math.max(0, cap - n) + " left";
  return n + "/" + cap;
}

/** Headroom as a percentage, 0–100 (monolith `__echPct`, L12876). */
export function echPct(value: string | undefined, rule?: GuardrailField | null): number {
  const cap = capOf(rule);
  if (!cap) return 0;
  return Math.max(0, Math.min(100, Math.round(((value ?? "").length / cap) * 100)));
}

/**
 * THE enforcement primitive. Truncates to the rule's cap.
 *
 * Call it on every write and once more at render — that combination is what
 * makes overflow impossible rather than merely discouraged.
 */
export function clamp(value: string | undefined, rule?: GuardrailField | null): string {
  const s = value ?? "";
  const cap = capOf(rule);
  return cap && s.length > cap ? s.slice(0, cap) : s;
}

/** True when the value is at or past its cap (i.e. the UI should go red). */
export function isOver(value: string | undefined, rule?: GuardrailField | null): boolean {
  return capState(value, rule) === "over";
}

/** A block on the canvas that broke its own rules. */
export interface Violation {
  module: string;
  field: string;
  label: string;
  /**
   * WHICH RULE BROKE (2026-09-01). Was implicitly always "cap"; `pattern` joined it
   * when format checks moved into this sweep; `alt` (2026-09-02) is a missing image
   * alt text, counted by `echContactWarnings` and never produced here. Callers that
   * render a violation must branch on this rather than assuming a character count.
   */
  /** `"data"` (2026-09-09): a DE data fault surfaced as a WARNING rather than a block,
   *  because the source declares `gateMode: "warn"`. Same sentence either way. */
  kind: "cap" | "pattern" | "alt" | "data";
  /** the sentence to show. For a cap it is built here; for a pattern it is the
   *  author's own `patternMessage`, or the generic fallback. */
  message: string;
  /** cap violations only — absent on a pattern violation */
  cap?: number;
  over?: number;
  /**
   * TRUE WHEN THE VALUE CAME FROM THE FEED, NOT THE REQUESTER (2026-09-01, Jack's
   * option C). A fed value that breaks a format is worth saying — an inventory price
   * of `399990` against a `$399,990` rule ships wrong either way — but it is not the
   * requester's mistake, and a warning that reads like an accusation for something
   * they did not do is how people learn to ignore the warnings that matter. The UI
   * uses this to add the provenance line, not to suppress the message.
   */
  fed?: boolean;
}

/**
 * WAS THIS VALUE WRITTEN BY THE FEED, OR TYPED? 2026-08-31.
 *
 * Answered from `__socFed`, the map the fill already keeps of what it wrote — see
 * `echDynPatch`'s seed branch. Deliberately NOT a new flag: a second piece of state
 * saying the same thing would need clearing on every edit, and a stamp that outlives
 * the value it describes is the exact bug shape that produced two other defects today.
 *
 * The test is EQUALITY, not presence: the value counts as the feed's only while it is
 * still byte-for-byte what the feed wrote. One keystroke and the cap is back, with no
 * bookkeeping anywhere.
 */
export function isFedValue(data: Record<string, unknown> | undefined, key: string): boolean {
  try {
    const fed = JSON.parse(String((data || {}).__socFed || "{}")) as Record<string, unknown>;
    if (!(key in fed)) return false;
    return String(fed[key] ?? "") === String((data || {})[key] ?? "");
  } catch { return false; }
}

/**
 * Check a block's data against its guardrails. Returns [] when clean.
 * Used as the pre-submit gate — nothing should reach Content Builder over cap.
 */
export function violations(
  gr: Guardrails | undefined,
  data: Record<string, unknown>,
  /**
   * THE FULL DECLARED MAP, BECAUSE `gr.fields` IS THE WRONG ONE FOR FORMATS
   * (2026-09-01 — and this is the exact trap that made the 2026-08-25 fix look done
   * when it was not).
   *
   * Production calls this as `violations(grFor(t), data)`, and `grFor` returns
   * `echLimits` — filtered to fields a CAP may be enforced against, which drops
   * image/url/number/tel/select outright and drops anything without a `max` or a
   * `lines`+`cpl` pair. That is correct for clamping and useless for `pattern`, which
   * is meaningful on precisely the types that filter removes: a URL's shape, a price's
   * format, a phone number. 68 of Layne's 88 patterns sit on url/number/tel and would
   * never have appeared in `gr.fields` at all.
   *
   * So formats are checked against this map when the caller supplies it. Falling back
   * to `gr.fields` keeps every existing caller and test honest rather than silently
   * checking nothing.
   */
  patFields?: Record<string, GuardrailField>,
): Violation[] {
  if (!gr?.fields) return [];
  const out: Violation[] = [];
  for (const [key, rule] of Object.entries(patFields || gr.fields)) {
    if (!rule.pattern) continue;
    const pat = checkPattern(data[key], rule);
    if (pat.ok) continue;
    out.push({
      module: gr.module, field: key, label: rule.label || key,
      kind: "pattern", message: pat.message, fed: isFedValue(data, key),
    });
  }
  for (const [key, rule] of Object.entries(gr.fields)) {
    /* ── THE FORMAT CHECK RUNS FIRST, AND IT RUNS FOR EVERY FIELD (2026-09-01) ──────
       Layne, reopening a report we closed twice: 78 of 88 declared patterns did
       nothing. We had been fixing this at the RENDER site — teaching one more control
       to show the message — and missing a path each time, because a pattern's message
       can only appear where a control chose to draw a line for it. Two paths draw no
       such line at all (a fed url field, a fed read-only field) and the listing
       table's rows are not drawn by the field renderer at all: only their `Url`
       survives as a panel section, and Homesite/Plan/SqFt/Beds/Baths/Price are edited
       in place on the card by an inline input with no label, no counter and nowhere to
       put a message. All ten price patterns lived there.

       So the check moved HERE, to the sweep that walks the DATA map. This function
       already reached every one of those fields — that is why `max` fired on a row's
       Plan field and `pattern` did not — and it reaches any control someone adds next
       year without being told about it. What kept patterns out was purely the `!cap`
       bail below, which was written when a cap was the only rule this knew about: a
       price field declares a pattern and NO cap, so it left on the first line.

       Deliberately NOT exempted for a fed value, unlike the cap. An auto-filled string
       that breaks a format is still wrong in the email — an inventory price of `399990`
       renders as `399990` — whereas an auto-filled string that is merely LONG has no
       shorter correct form and flagging it hands the requester an error they cannot
       act on (see the cap exemption below). Same input, different rule, different
       answer. The provenance is carried on `fed` so the wording can say where the
       value came from. */
    const cap = capOf(rule);
    if (!cap) continue;
    /* ── AN AUTO-FILLED VALUE IS NOT A LENGTH SOMEONE CHOSE (2026-08-31, LEH pilot:
       "Community subheader block auto-populated the community name but an error message
       was shown that it is too long"). The cap exists to keep HAND-TYPED copy inside the
       artwork. A community name pulled from the record has no shorter correct form, so
       flagging it hands the requester an error they cannot act on — which only teaches
       divisions to ignore the warnings that DO matter. Typed copy keeps its cap: the
       exemption lapses the moment the value stops matching what the feed wrote. */
    if (isFedValue(data, key)) continue;
    const n = String(data[key] ?? "").length;
    if (n > cap) {
      out.push({
        module: gr.module, field: key, label: rule.label || key,
        kind: "cap", cap, over: n - cap,
        message: (n - cap) + " character" + (n - cap === 1 ? "" : "s") + " over the " + cap + "-character cap",
      });
    }
  }
  return out;
}

/**
 * Substitute a block's tokens with its (clamped) values.
 *
 * A token appears everywhere its value is needed — including inside the
 * Outlook VML button as well as the standard <a> — so one field fills both and
 * they can never drift apart. Empty <p> collapse so optional slots leave no gap.
 */
export function renderTokens(
  html: string,
  gr: Guardrails | undefined,
  data: Record<string, unknown>,
  escapeFn: (s: string) => string,
): string {
  let out = html;
  const fields = gr?.fields ?? {};
  for (const [key, rule] of Object.entries(fields)) {
    const token = rule.token || `{{${key}}}`;
    const value = clamp(String(data[key] ?? ""), rule);
    out = out.split(token).join(escapeFn(value));
  }
  return out.replace(/<p\b[^>]*>\s*<\/p>/gi, "");
}
